"use server"

import { cookies } from "next/headers"
import { redirect } from "next/navigation"
import bcrypt from "bcryptjs"
import { z } from "zod"

import { prisma } from "@/lib/prisma"
import { createOtpForEmail, OTP_EMAIL_COOKIE, sendOtpEmail } from "@/lib/otp"

const schema = z.object({
  email: z.string().email("อีเมลไม่ถูกต้อง"),
  password: z.string().min(1, "กรุณากรอกรหัสผ่าน"),
})

export type LoginState = {
  error: string | null
  fieldErrors?: Record<string, string[]>
}

/**
 * Step 1 of login: verify email + password, then issue an OTP and hand off to
 * the /otp page for the second factor (REQ-004). No session is created here.
 */
export async function loginAction(
  _prev: LoginState,
  formData: FormData,
): Promise<LoginState> {
  const parsed = schema.safeParse({
    email: formData.get("email"),
    password: formData.get("password"),
  })

  if (!parsed.success) {
    return {
      error: "ข้อมูลไม่ถูกต้อง",
      fieldErrors: z.flattenError(parsed.error).fieldErrors,
    }
  }

  const { email, password } = parsed.data

  const user = await prisma.user.findUnique({ where: { email } })
  // Same generic message whether the email or the password is wrong.
  if (!user || !user.password || user.status !== "ACTIVE") {
    return { error: "อีเมลหรือรหัสผ่านไม่ถูกต้อง" }
  }

  const valid = await bcrypt.compare(password, user.password)
  if (!valid) {
    return { error: "อีเมลหรือรหัสผ่านไม่ถูกต้อง" }
  }

  // Password OK → generate + deliver OTP, remember which email is mid-login.
  const { code } = await createOtpForEmail(email)
  await sendOtpEmail(email, code)

  const cookieStore = await cookies()
  cookieStore.set(OTP_EMAIL_COOKIE, email, {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: 600, // 10 min, matches OTP TTL
  })

  redirect("/otp")
}
